import { createContext, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [userdetails, setUserDetails_] = useState(() => {
    try {
      const stored = localStorage.getItem("userdetails");
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error("Failed to parse userdetails from localStorage:", err);
      localStorage.removeItem("userdetails");
      return null;
    }
  });

  /**
   * isAuthLoading — true during the very first render while React is
   * committing the initial hydrated state from localStorage.
   * ProtectedRoute renders nothing until false, preventing the race where
   * group_names is seen as empty before context propagates to consumers.
   */
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    setIsAuthLoading(false);
  }, []);

  // token is derived from userdetails — truthy when authenticated, null when not.
  // Kept in context so the ~100 components using `if (token && ...)` as their
  // data-fetch trigger continue to work without modification.
  // Never stored in localStorage — userdetails is the persistent auth indicator.
  const token = userdetails ? true : null;

  // no-op — call sites in Login/Logout/components are kept as-is
  const setToken = () => {};

  const setUserDetails = (ud) => setUserDetails_(ud);

  useEffect(() => {
    if (userdetails && typeof userdetails === 'object') {
      localStorage.setItem('userdetails', JSON.stringify(userdetails));
    } else {
      localStorage.removeItem('userdetails');
    }
  }, [userdetails]);

  const logout = () => {
    setUserDetails_(null);
    // JWT cookies cleared server-side. Clean up all localStorage auth keys.
    localStorage.removeItem('token');     // stale DRF token from pre-migration sessions
    localStorage.removeItem('userdetails');
    localStorage.removeItem('tenant_schema');
  };

  const contextValue = useMemo(
    () => ({
      token,
      setToken,
      userdetails,
      setUserDetails,
      logout,
      isAuthLoading,
    }),
    [token, userdetails, isAuthLoading]
  );

  // Provide the authentication context to the children components
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
